import { ApolloClient, DefaultOptions } from 'apollo-client'
import { fromPromise } from 'apollo-link'
import { createHttpLink } from 'apollo-link-http'
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory'
import {
  closedPositionsQuery,
  closedPositionsQueryVariables,
  getAllowedLeveragesQuery,
  getAllowedLeveragesQueryVariables,
  viewerQuery,
  getFavouritesQuery,
  updateFavouritesMutation,
  updateFavouritesMutationVariables,
} from '../graphql/schema'
import { Observable } from 'rxjs/Observable'
import { Observer } from 'rxjs/Observer'
import { onError } from 'apollo-link-error'
import { I18n, SubTranslationObject } from 'react-redux-i18n'
import { GraphQLError } from 'graphql'
import { toast } from 'react-toastify'
const graphqlDocuments = require('../graphql/documents.json')
const introspectionQueryResultData = require('../graphql/fragmentTypes.json')

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
})

const errorCallback = (error: GraphQLError) => {
  toast.error(error.message, { autoClose: false })
}

export class GraphQLRepository {
  private static instance: GraphQLRepository
  private url: string = ''
  private token?: string
  private apolloClient?: ApolloClient<any>

  private constructor() {}

  public static get Instance() {
    return this.instance || (this.instance = new this())
  }

  public getClosedPositions(params: closedPositionsQueryVariables) {
    return Observable.create((observer: Observer<closedPositionsQuery>) => {
      this.ApolloClient.watchQuery<closedPositionsQuery>({
        query: graphqlDocuments['src/graphql/closedPositions.graphql'],
        variables: params,
      }).subscribe(
        ({ data, errors }) => {
          if (errors) {
            errors.forEach((error) => {
              errorCallback(error)
            })
          }
          observer.next(data)
        },
        (error) => observer.error(error),
        () => observer.complete()
      )
    })
  }

  public getViewer() {
    return Observable.create((observer: Observer<viewerQuery>) => {
      this.ApolloClient.watchQuery<viewerQuery>({
        query: graphqlDocuments['src/graphql/viewer.graphql'],
      }).subscribe(
        ({ data, errors }) => {
          if (errors) {
            errors.forEach((error) => {
              errorCallback(error)
            })
          }
          observer.next(data)
        },
        (error) => observer.error(error),
        () => observer.complete
      )
    })
  }

  // public getBondConfigList(): Observable<BondConfig> {
  //     let headers: HeadersInit = {
  //         'Content-Type': 'application/json',
  //     };
  //     if(this.token) {

  //         headers = { ...headers, Authorization: this.token};
  //     }
  //     return Observable.create((observer: Observer<BondConfig>) => {
  //         fetch(this.url + '/api/external/bond-config', {
  //             method: 'GET',
  //             credentials: 'include'
  //         }).then(response => response.json())
  //             .then(data => {
  //                 observer.next(data);
  //                 observer.complete();
  //             })
  //             .catch(err => observer.error(err));
  //     });
  // }

  public getAllowedLeverages(params: getAllowedLeveragesQueryVariables) {
    return Observable.create((observer: Observer<getAllowedLeveragesQuery>) => {
      this.ApolloClient.watchQuery<getAllowedLeveragesQuery>({
        query: graphqlDocuments['src/graphql/getAllowedLeverages.graphql'],
        variables: params,
      }).subscribe(
        ({ data, errors }) => {
          if (errors) {
            errors.forEach((error) => {
              errorCallback(error)
            })
          }
          observer.next(data)
        },
        (error) => observer.error(error),
        () => observer.complete()
      )
    })
  }

  public getTranslations(lang?: string) {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }
    if (this.token) headers.Authorization = this.token
    return Observable.create((observer: Observer<SubTranslationObject>) => {
      const apiUrl = lang
        ? '/api/translations/webtrader?locale=' + lang
        : '/api/translations/webtrader'

      fetch(this.url + apiUrl, {
        method: 'GET',
        // headers,
        // credentials: 'same-origin'
        credentials: 'include',
      })
        .then((response) => response.json())
        .then((data) => {
          observer.next(data)
          observer.complete()
        })
        .catch((err) => observer.error(err))
    })
  }

  public getFavourites() {
    return Observable.create((observer: Observer<getFavouritesQuery>) => {
      this.ApolloClient.watchQuery<getFavouritesQuery>({
        query: graphqlDocuments['src/graphql/getFavourites.graphql'],
      }).subscribe(
        ({ data, errors }) => {
          if (errors) {
            errors.forEach((error) => {
              errorCallback(error)
            })
          }
          observer.next(data)
        },
        (error) => observer.error(error),
        () => observer.complete
      )
    })
  }

  public updateFavourites(params: updateFavouritesMutationVariables) {
    return Observable.create((observer: Observer<updateFavouritesMutation>) => {
      this.ApolloClient.mutate<updateFavouritesMutation>({
        mutation: graphqlDocuments['src/graphql/updateFavourites.graphql'],
        variables: params,
      })
        .then((data: any) => observer.next(data))
        .catch((error: any) => observer.error(error))
        .finally(() => observer.complete())
    })
  }

  set Url(value: string) {
    this.url = value
  }

  set Token(value: string) {
    this.token = value
  }

  get ApolloClient(): ApolloClient<any> {
    const defaultOptions: DefaultOptions = {
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
    }

    if (!this.apolloClient) {
      this.apolloClient = new ApolloClient({
        link: onError(({ networkError, graphQLErrors, operation, forward }) => {
          if (networkError) {
            if ([401, 404].includes((networkError as any)?.statusCode)) {
              return fromPromise(
                fetch(`${this.url}/auth/create-demo-account`, {
                  credentials: 'include',
                  method: 'POST',
                })
              ).flatMap(() => {
                return forward(operation)
              })
            }

            console.log(I18n.t('graphQLNetworkError', { networkError }))
            errorCallback({
              ...networkError,
              message: I18n.t('graphQLNetworkError', { networkError }),
            })
          }

          if (graphQLErrors) {
            graphQLErrors.forEach(({ message, locations, path }) => {
              console.log(
                I18n.t('graphQLError', {
                  message,
                  location: locations && locations.length ? locations.join(',') : '',
                  path,
                })
              )
              errorCallback({
                ...graphQLErrors[0],
                message: I18n.t('graphQLError', {
                  message,
                  location: locations && locations.length ? locations.join(',') : '',
                  path,
                }),
              })
            })
          }
        }).concat(
          createHttpLink({
            uri: `${this.url}/graphql`,
            credentials: 'include',
          })
        ),
        cache: new InMemoryCache({ fragmentMatcher }),
        defaultOptions: defaultOptions,
      })
    }
    return this.apolloClient
  }
}
